import { forwardRef } from 'react';

import { cva } from 'class-variance-authority';
import { motion } from 'motion/react';
import { twMerge } from 'tailwind-merge';

import { ModalLeftButton } from './ModalLeftButton';
import { ModalRightButton } from './ModalRightButton';
import { ModalTemplateTitle } from './ModalTemplateTitle';
import { TModalTemplate } from './types';

const modalVariants = cva(
  'flex flex-col overflow-hidden h-full w-full bg-base-500 rounded-[24px] md:w-screen',
  {
    variants: {
      fullHeight: {
        true: 'max-h-[90dvh]',
        false: 'max-h-[min(600px,90dvh)]',
      },
      fullWidth: {
        true: 'md:max-w-[630px]',
        false: 'md:max-w-[342px]',
      },
      dashboard: {
        true: 'rounded-[32px]',
        false: 'rounded-[24px]',
      },
    },
    defaultVariants: {
      fullHeight: false,
    },
  },
);

export const ModalTemplate = forwardRef<HTMLDivElement, TModalTemplate>(
  (
    {
      id,
      title,
      description,
      HeaderComponent,
      isDashboard,

      isHasCloseBtn = false,
      LeftBtn,
      RightBtn,
      onBack,
      onClose,

      className,
      classNames = {},

      isFullHeight = false,
      isFullWidth = false,
      isHasAnimationLayout = true,
      children,
    },
    ref,
  ) => {
    const isHasButtons = Boolean(LeftBtn || RightBtn || isHasCloseBtn);
    const isHasTitle = Boolean(title || description || HeaderComponent);

    return (
      <motion.div
        ref={ref}
        layout={isHasAnimationLayout}
        transition={{
          layout: {
            type: 'spring',
            stiffness: 500,
            damping: 45,
            mass: 0.8,
          },
        }}
        className={twMerge(
          modalVariants({
            fullHeight: isFullHeight,
            fullWidth: isFullWidth,
            dashboard: isDashboard,
          }),
          classNames.wrapper,
        )}
      >
        <div
          className={twMerge(
            'relative flex h-full flex-col overflow-y-auto',
            classNames.container,
          )}
        >
          {(LeftBtn || onBack) && (
            <ModalLeftButton {...(LeftBtn || {})} onBack={onBack} />
          )}

          {(title || description || HeaderComponent) &&
            (HeaderComponent || (
              <ModalTemplateTitle
                id={id}
                title={title}
                description={description}
                isHasButtons={isHasButtons}
                titleClassName={classNames.title}
                descriptionClassName={classNames.description}
                className={classNames.header}
              />
            ))}
          {(RightBtn || isHasCloseBtn) && (
            <ModalRightButton
              {...(RightBtn || {})}
              id={id}
              isHasCloseBtn={isHasCloseBtn}
              onClose={onClose}
            />
          )}
          <div
            className={twMerge(
              'flex w-full flex-1 flex-col',
              className,
              classNames.content,
            )}
          >
            {children}
          </div>
        </div>
      </motion.div>
    );
  },
);
